import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getJobs, startJobsStream, stopJobsStream } from '../../redux/actions';

const ACTIVE_STATUSES = ['pending', 'running'];
const FINAL_STATUSES = ['completed', 'failed', 'cancelled', 'timed_out'];
const DISPLAY_WINDOW_MS = 10000;

const formatLabel = (value) => {
    if (!value || typeof value !== 'string') {
        return '';
    }
    return value
        .split('_')
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
};

const resolveStatusBadge = (status) => {
    const baseStyle = {
        borderRadius: 999,
        padding: '2px 10px',
        fontSize: 12,
        fontWeight: 600,
        lineHeight: 1.3,
    };

    switch (status) {
        case 'pending':
            return { ...baseStyle, backgroundColor: '#fff7e6', color: '#b06a00' };
        case 'running':
            return { ...baseStyle, backgroundColor: '#e8f1ff', color: '#0b57d0' };
        case 'completed':
            return { ...baseStyle, backgroundColor: '#e7f9ed', color: '#1b7a3f' };
        case 'failed':
            return { ...baseStyle, backgroundColor: '#fdecea', color: '#b02a37' };
        case 'cancelled':
        case 'timed_out':
            return { ...baseStyle, backgroundColor: '#f1f3f5', color: '#5c6f82' };
        default:
            return { ...baseStyle, backgroundColor: '#eef2f7', color: '#4b5563' };
    }
};

const jobDisplayName = (job) => {
    return job?.description || job?.message || formatLabel(job?.jobType) || 'Job';
};

const jobDisplayType = (job) => formatLabel(job?.jobType);

const getRelevantTimestamp = (job) =>
    job?.completedAt || job?.updatedAt || job?.startedAt || job?.createdAt;

const containerStyle = {
    position: 'fixed',
    bottom: 24,
    right: 24,
    width: 320,
    maxWidth: '92vw',
    maxHeight: 'calc(100vh - 48px)',
    backgroundColor: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 12px 30px rgba(15, 23, 42, 0.08)',
    border: '1px solid #e9edf2',
    padding: '12px 14px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    zIndex: 1300,
};

const headerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontWeight: 700,
    fontSize: 14,
    color: '#1f2937',
    marginBottom: 8,
};

const itemStyle = {
    padding: '8px 0',
    borderBottom: '1px solid #f1f5f9',
};

const lastItemStyle = {
    paddingTop: 8,
};

const nameStyle = {
    fontSize: 13,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 4,
    lineHeight: 1.3,
};

const metaRowStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
};

const typeStyle = {
    fontSize: 12,
    color: '#6b7280',
    flex: 1,
    marginRight: 6,
};

const listStyle = {
    overflowY: 'auto',
    paddingRight: 4,
    minHeight: 0,
};

const ActiveJobsPopup = () => {
    const dispatch = useDispatch();
    const jobs = useSelector((state) => state.Jobs?.jobs || []);
    const user = useSelector((state) => state.Auth?.user);

    const [now, setNow] = useState(Date.now());
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (!user?.token) {
            dispatch(stopJobsStream());
            return undefined;
        }

        dispatch(getJobs({ pageSize: 50, sortBy: 'updatedAtDesc' }));
        dispatch(startJobsStream());

        return () => {
            dispatch(stopJobsStream());
        };
    }, [user?.token, dispatch]);

    const visibleJobs = useMemo(() => {
        return (jobs || []).filter((job) => {
            if (!job) {
                return false;
            }
            const status = job.status;
            if (ACTIVE_STATUSES.includes(status)) {
                return true;
            }
            if (FINAL_STATUSES.includes(status)) {
                const timestamp = getRelevantTimestamp(job);
                if (!timestamp) {
                    return false;
                }
                const elapsed = now - new Date(timestamp).getTime();
                return elapsed <= DISPLAY_WINDOW_MS;
            }
            return false;
        });
    }, [jobs, now]);

    if (!user?.token || visibleJobs.length === 0 || dismissed) {
        return null;
    }

    return (
        <div style={containerStyle}>
            <div style={headerStyle}>
                <span>Job activity</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 999, backgroundColor: '#22c55e' }}></span>
                    <button
                        type="button"
                        aria-label="Close jobs popup"
                        onClick={() => setDismissed(true)}
                        style={{
                            border: 'none',
                            background: 'transparent',
                            color: '#6b7280',
                            cursor: 'pointer',
                            padding: 4,
                            lineHeight: 1,
                            fontWeight: 700,
                        }}>
                        ×
                    </button>
                </div>
            </div>
            <div style={listStyle}>
                {visibleJobs.map((job, index) => {
                    const statusStyle = resolveStatusBadge(job.status);
                    const isLast = index === visibleJobs.length - 1;
                    return (
                        <div key={job.id || index} style={isLast ? lastItemStyle : itemStyle}>
                            <div style={nameStyle} title={jobDisplayName(job)}>
                                {jobDisplayName(job)}
                            </div>
                            <div style={metaRowStyle}>
                                <span style={typeStyle}>{jobDisplayType(job)}</span>
                                <span style={statusStyle}>{formatLabel(job.status)}</span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ActiveJobsPopup;
